import React, { useCallback, useEffect, useState } from "react";
import { Cloud, CloudOff, CloudUpload, AlertTriangle } from "lucide-react";
import { isInstalledAppEnvironment } from "@/lib/device";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { listStockQueue, STOCK_ACTION_QUEUED } from "@/lib/stockSync";
import { listQueuedEvents, LOCAL_EVENT_APPLIED } from "@/lib/eventEngine";
import { EventLifecycle } from "@/lib/events";
import { EVENT_SYNC_FLUSHED } from "@/lib/eventSync";
import { SyncView } from "@/components/inventory/StockLocationsPanel";

/**
 * Session 41.5 — DeviceSyncPill (Owner / Manager header).
 *
 * Replaces the "Sync" card that used to live inside Inventory → Stock
 * Locations. Sits next to the mesh pill (same size, font and colour
 * variants as MeshStatusPill so the two read as one set) and — exactly like
 * the mesh pill on the Staff / Retail dashboards — renders ONLY inside the
 * installed apps (Capacitor APK / Tauri EXE). On the regular web view it
 * renders nothing. Tapping it opens the full sync workspace (queue ·
 * retry / discard · server exceptions).
 *
 *   Green (lan)     → online, queue empty                    "Synced"
 *   Amber (cloud)   → events waiting / sending               "3 to sync"
 *   Red   (offline) → rejected rows or the device is offline "Offline · 2 queued"
 */
export function DeviceSyncPill({ http, authHeaders, canManage = true, onStockChanged, className = "" }) {
  const [counts, setCounts] = useState({ waiting: 0, failed: 0, stockWaiting: 0 });
  const [online, setOnline] = useState(typeof navigator === "undefined" ? true : navigator.onLine !== false);
  const [open, setOpen] = useState(false);
  const isNativeApp = React.useMemo(() => isInstalledAppEnvironment(), []);

  const refresh = useCallback(async () => {
    try {
      const rows = await listQueuedEvents();
      const st = (r) => String((r && r._meta && r._meta.status) || "");
      const waiting = rows.filter((r) => st(r) === EventLifecycle.LOCAL || st(r) === EventLifecycle.PENDING).length;
      const failed = rows.filter((r) => st(r) === EventLifecycle.FAILED).length;
      let stockWaiting = 0;
      try { const q = await listStockQueue(); stockWaiting = (q.counts.local || 0) + (q.counts.pending || 0); } catch (_) { /* noop */ }
      setCounts({ waiting, failed, stockWaiting });
    } catch (_) { /* IDB unavailable — keep last */ }
  }, []);

  useEffect(() => {
    refresh();
    const on = () => { setOnline(true); refresh(); };
    const off = () => { setOnline(false); refresh(); };
    window.addEventListener(EVENT_SYNC_FLUSHED, refresh);
    window.addEventListener(STOCK_ACTION_QUEUED, refresh);
    window.addEventListener(LOCAL_EVENT_APPLIED, refresh);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    const iv = setInterval(refresh, 10000);
    return () => {
      window.removeEventListener(EVENT_SYNC_FLUSHED, refresh);
      window.removeEventListener(STOCK_ACTION_QUEUED, refresh);
      window.removeEventListener(LOCAL_EVENT_APPLIED, refresh);
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
      clearInterval(iv);
    };
  }, [refresh]);

  // Same three variants (and exact classes) as MeshStatusPill.
  let variant = "lan";
  let Icon = Cloud;
  let label = "Synced";
  if (!online) {
    variant = "offline";
    Icon = CloudOff;
    label = counts.waiting ? `Offline · ${counts.waiting} queued` : "Offline";
  } else if (counts.failed) {
    variant = "offline";
    Icon = AlertTriangle;
    label = `Sync · ${counts.failed} rejected${counts.waiting ? ` · ${counts.waiting} waiting` : ""}`;
  } else if (counts.waiting) {
    variant = "cloud";
    Icon = CloudUpload;
    label = `Sync · ${counts.waiting} waiting`;
  }

  const styles = {
    lan:     "border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100 dark:border-emerald-700/60 dark:bg-emerald-950/40 dark:text-emerald-200 dark:hover:bg-emerald-950/70",
    cloud:   "border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100 dark:border-amber-700/60 dark:bg-amber-950/40 dark:text-amber-200 dark:hover:bg-amber-950/70",
    offline: "border-rose-300 bg-rose-50 text-rose-800 hover:bg-rose-100 dark:border-rose-700/60 dark:bg-rose-950/40 dark:text-rose-200 dark:hover:bg-rose-950/70",
  };

  // Web view (regular browser) → no pill at all, exactly like MeshStatusPill.
  if (!isNativeApp) return null;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="device-sync-pill"
        data-variant={variant}
        className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-semibold tabular-nums transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--accent))]/40 ${styles[variant]} ${className}`}
        aria-label={`Device sync: ${label}. Tap to open the sync workspace.`}
        title="This device's sync queue"
      >
        <Icon className="h-3.5 w-3.5" aria-hidden="true" />
        {label}
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[92vh] overflow-y-auto rounded-[24px] sm:max-w-4xl" data-testid="device-sync-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Space_Grotesk'] text-2xl">Device sync</DialogTitle>
            <DialogDescription>What this device still has to send, what the server applied, and the exceptions a manager must resolve. Nothing is lost while offline — every action is saved here first.</DialogDescription>
          </DialogHeader>
          {open ? <SyncView http={http} authHeaders={authHeaders} canManage={canManage} onStockChanged={async () => { await refresh(); if (onStockChanged) await onStockChanged(); }} /> : null}
        </DialogContent>
      </Dialog>
    </>
  );
}

export default DeviceSyncPill;
